import {
	useEffect, useState,
} from 'react';
import Camera from 'components/camera';
import {
	Textarea,
} from 'components/form';
import Button, {
	ButtonColor,
} from 'components/button';
import './deviation.scss';
import {
	DeviationEnum,
} from 'enum';
import {
	useDeviationService,
} from '../useDeviationService';
import {
	Assignment,
	DeviationModel,
	Image,
} from 'models';
import ModalStack from '../modalstack/ModalStack';
import {
	ModalWithFooter,
} from 'components/modal';
import LoadingWheel from 'components/loadingWheel/LoadingWheel';

export const getDeviationHeader = (deviation?: DeviationEnum): string => {
	switch (deviation) {
		case DeviationEnum.DAMAGED_ITEM: return 'Skade på tralle';
		case DeviationEnum.MISSING_ITEM: return 'Tralle ikke tilgjengelig på avtalt felt';
		case DeviationEnum.DELAYED_DEPARTURE: return 'Forsinket avgang fra havn';
		case DeviationEnum.DELAYED_ARRIVAL: return 'Forsinket ankomst til havn';
		case DeviationEnum.PARKING_LOT_OCCUPIED: return 'Avtalt trallefelt opptatt';
		case DeviationEnum.POWER_SUPPLY_DAMAGED: return 'Feil på strømtilførsel';
		case DeviationEnum.POWER_SUPPLY_NOT_CONNECTED: return 'Strømtilførsel mangler (tralle har stått uten strøm)';
		case DeviationEnum.POWER_SUPPLY_NO_FUEL: return 'Aggregat tom for diesel';
		default: return '';
	}
};

interface Props  {
	code: DeviationEnum;
	assignment: Assignment;
	/**
	 * In case we wish to edit existing deviation
	 */
	deviation?: DeviationModel;
	onClose: () => void;
	show: boolean;
	setShow: (show: boolean) => void;
	setHideAllModals: (show: boolean) => void;
}

//Test fra: https://itnext.io/accessing-the-webcam-with-javascript-and-react-33cbe92f49cb
const Deviation = ({
	code,
	assignment,
	onClose,
	show = false,
	setShow,
	setHideAllModals,
}: Props): JSX.Element => {
	const {
		table,
	} = useDeviationService();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [deviation, setDeviation] = useState<DeviationModel>();
	const [showHideConfirmation, setShowHideConfirmation] = useState(false);
	const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false);
	const [id, setId] = useState<number | null | undefined>();
	const [comment, setComment] = useState<string>('');
	const {
		upsert,
		remove,
		getImages,
		postImages,
		removeImages,
		isLoading,
	} = useDeviationService();
	const [images, setImages] = useState<Image[]>([]);
	const [imagesToRemove, setImagesToRemove] = useState<Image[]>([]);

	useEffect(() => {
		setIsOpen(show);
	}, [show]);

	useEffect(() => {
		if (show) {
			table.where('code').equals(code).and(value =>
				value.transportId === assignment.id
			).toArray()
				.then((data: DeviationModel[]) => {
					if (data.length) {
						const [dev] = data;
						setDeviation(dev);
						setId(dev.id);
						setComment(dev.comment)
						setImages(dev.images);
						setIsLoadingImages(true);
						getImages(dev)
							.then(images => {
								setImages(images);
								setIsLoadingImages(false);
							})
							.catch(error => {
								setIsLoadingImages(false);
								console.error(error);
							});
					}
				})
				.catch(console.error);
		}
	}, [assignment, code, show, table]);

	const renderCameraRollOrLoading = () => {
		if (isLoadingImages) {
			return (<LoadingWheel message="Laster inn bilder …"/>);
		}

		if (isLoading) {
			const activeAction = (deviation || id) ? 'Oppdaterer' : 'Registrerer';
			return (<LoadingWheel message={`${activeAction}  avviket…`}/>);
		}
		return (
			<Camera
				initialImages={images}
				onPhotoTaken={(img) => setImages(img)}
				onImageRemoved={image => setImagesToRemove([image, ...imagesToRemove])}
			/>
		);
	}

	return (
		<>
			<ModalWithFooter
				show={showHideConfirmation}
				title="Er du sikker?"
				onConfirmation={() => {
					remove(deviation?.transportId, deviation?.id || id)
						.then(() => {
							setShowHideConfirmation(false);
							setIsOpen(false);
							onClose();
						})
						.catch(error => {
							// TODO: There should be error handeling here
							console.error(error);
						});
				}}
				onClose={() => {
					setShowHideConfirmation(false);
					setIsOpen(true);
				}}
			>
				Du er i ferd med å slette avviket.
			</ModalWithFooter>

			<ModalStack
				header={getDeviationHeader(code)}
				showHide={isOpen}
				setShowHide={() => {
					setIsOpen(false);
					setShow(false);
				}}
				setHideAll={setHideAllModals}
			>

				<div className="damaged-goods container">
					{show && renderCameraRollOrLoading()}
					{/* TODO: Implement support for correct typography*/}
					<Textarea
						rowValue={comment}
						onChange={setComment}
						onClear={() => setComment('')}
						placeholderText="Beskrivelse"
						readOnly={isLoading}
						usePlaceholderAsLabel
					/>
					<div className="button-row">
						{(deviation || id) &&
                <Button
                	color={ButtonColor.DANGER}
                	onClick={() => {
                		setShowHideConfirmation(true);
                		setIsOpen(false);
                	}}
                >
                    Slett
                </Button>
						}
						<Button
							className="submit-deviation"
							onClick={async () => {
								/**
								 * We do not wish to remove or update images
								 * if the deviation is yet to be created.
								 */
								if (deviation) {
									await removeImages(deviation, imagesToRemove);
									await postImages(deviation, images);
								}
								/**
								 * We do not want to update the deviation if there are no changes to
								 * the comment.
								 * Empty comments should also be allowed.
								 */
								if (!comment || (comment && deviation?.comment !== comment) ) {
									upsert({
										id: deviation?.id || id || undefined,
										transportId: assignment.id,
										comment,
										timestamp: deviation?.timestamp || +new Date(),
										images,
										code,
									})
										.then(() => {
											setShowHideConfirmation(false);
											setIsOpen(false);
											onClose();
										})
										.catch(error => {
											// TODO: There should be error handeling here
											console.error(error);
										});
								}
								else {
									setShowHideConfirmation(false);
									setIsOpen(false);
									onClose();
								}
							}}
							disabled={isLoading}
						>
							{(deviation || id) ? 'Oppdater' : 'Registrer'}
						</Button>
					</div>
				</div>
			</ModalStack>
		</>
	);
};

export default Deviation;
